import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchShowDoctors } from "../../features/Doctors/ShowDoctors";
import { motion } from "framer-motion";
import Loading from "../Loading";
import { useTranslation } from "react-i18next";

function ShowDoctor() {
  const { id, team } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    dispatch(fetchShowDoctors({ id, team }));
  }, [dispatch, id, i18n.language, team]);

  const { doctors, loading } = useSelector((state) => state.showDoctors);

  const doctorName = doctors?.name;
  const specialty = doctors?.speciality;
  const description = doctors?.description;
  const years = doctors?.year_of_service;
  const noSurgery = doctors?.surgery;
  const imageUrl = process.env.REACT_APP_IMG_URL + (doctors?.image || "");

  function year(datetime) {
    const date = new Date(datetime);
    const today = new Date();
    const yearsAgo = today.getFullYear() - date.getFullYear();
    return yearsAgo;
  }

  function handleAttachment() {
    if (!doctors?.attachment) return;
    window.open(process.env.REACT_APP_IMG_URL + doctors?.attachment, '_blank');
  }

  function handleAttachmentTeam(attachment) {
    if (!attachment) return;
    window.open(process.env.REACT_APP_IMG_URL + attachment, '_blank');
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="grid w-10/12 grid-cols-1 lg:grid-cols-2 items-center py-6 lg:w-11/12 mx-auto lg:px-5 mt-10 lg:gap-12"
        >
          {/* 🔹 Image Section */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative flex w-11/12 justify-center items-center"
          >
            {/* Decorative Rotated Border */}
            <motion.div
              initial={{ rotate: 0, scale: 0.8, opacity: 0 }}
              animate={{ rotate: 25, scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className={`absolute overflow-hidden mt-10 w-64 h-64 md:w-72 md:h-72 border-4 border-[#079120] rounded-[3rem] shadow-sm -z-50`}
            ></motion.div>

            {/* Doctor Image */}
            <div className="overflow-hidden group">
              {doctors?.image === null ? (
                <div className="z-50 h-[400px] object-cover rounded-2xl transition-transform group-hover:scale-105 duration-700">
                  <img
                    src={process.env.PUBLIC_URL + "/assets/user.png"}
                    alt="Doctor"
                    className="w-72 h-72 left-4 object-cover rounded-2xl transition-transform group-hover:scale-105 duration-700"
                  />
                </div>
              ) : (
                <motion.img
                  src={imageUrl}
                  alt="Doctor"
                  className="z-50 h-[400px] object-cover rounded-2xl transition-transform group-hover:scale-105 duration-700"
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                />
              )}
            </div>

            {/* Green Bar at Bottom */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
              className="absolute bottom-0 transform w-[80%] h-2 bg-[#079120] rounded-full"
            ></motion.div>
          </motion.div>

          {/* 🔹 Information Section */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="space-y-6"
          >
            <motion.div>
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-xl mt-10 lg:text-5xl font-extrabold text-zinc-900 dark:text-white leading-tight transition-colors duration-300"
              >
                {doctorName}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-zinc-600 dark:text-zinc-300 mt-3 transition-colors duration-300"
              >
                {specialty}
              </motion.p>
            </motion.div>

            {description === null ? (
              ""
            ) : (
              <div className="flex flex-col">
                <p className="text-2xl font-bold text-zinc-900 dark:text-white transition-colors duration-300">{t("description")}</p>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-zinc-600 dark:text-zinc-300 leading-relaxed text-lg transition-colors duration-300"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </div>
            )}

            {/* Statistics */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-col gap-10 pt-1"
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <p className="text-zinc-500 dark:text-zinc-400 text-lg transition-colors duration-300">{t("YearExp")}</p>
                <p className="text-zinc-900 dark:text-white font-bold text-2xl transition-colors duration-300">
                  {year(years)}+
                </p>
              </motion.div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <p className="text-zinc-500 dark:text-zinc-400 text-lg transition-colors duration-300">{t("surgeries")} </p>
                <p className="text-zinc-900 dark:text-white font-bold text-2xl transition-colors duration-300">{noSurgery}</p>
              </motion.div>
            </motion.div>

            {/* 🔹 Improved CV Button Section */}
            <div className="flex flex-wrap gap-4 mt-6">
              {doctors?.doctors?.length > 0 ? (
                doctors.doctors.map((doctor, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAttachmentTeam(doctor?.attachment)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#079120] hover:bg-green-700 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="max-w-[120px] truncate">
                      {doctor?.name?.[i18n.language]?.substring(0, 59) || t("Doctor")}
                      {doctor?.name?.[i18n.language]?.length > 59 ? "..." : ""}
                    </span>
                    <span className="font-semibold">{t("CV")}</span>
                  </motion.button>
                ))
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAttachment}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#079120] hover:bg-green-700 text-white font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("View_CV")}</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default ShowDoctor;